/**
 * Gap Analysis Redux Slice
 * Manages state for gap analysis, MITRE coverage, and trend data
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  GapAnalysisState,
  MitreGapReport,
  MitreTactic,
  TechniqueGap
} from '../types';
import { gapAnalysisApi } from '../services/gapAnalysisApi';

const initialState: GapAnalysisState = {
  currentReport: null,
  heatmapData: [],
  clusters: [],
  trends: [],
  statistics: null,
  loading: false,
  error: null,
  selectedTactic: null,
  selectedTechnique: null,
  lastUpdated: undefined
};

/**
 * Async thunk to fetch comprehensive gap report
 */
export const fetchGapReport = createAsyncThunk(
  'gapAnalysis/fetchReport',
  async (
    {
      tenantId,
      timeRange = '7d'
    }: { tenantId: string; timeRange?: '24h' | '7d' | '30d' | '90d' },
    { rejectWithValue }
  ) => {
    try {
      const report = await gapAnalysisApi.getGapReport(tenantId, timeRange);
      const heatmapData = await gapAnalysisApi.getCoverageHeatmap(tenantId, timeRange);
      const clusters = await gapAnalysisApi.getAlertClusters(tenantId, timeRange);
      const trends = await gapAnalysisApi.getUncoveredAlertsTrend(tenantId, timeRange);
      const statistics = await gapAnalysisApi.getCoverageStatistics(tenantId, timeRange);

      return {
        report,
        heatmapData,
        clusters,
        trends,
        statistics
      };
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch gap report'
      );
    }
  }
);

/**
 * Async thunk to fetch critical gaps
 */
export const fetchCriticalGaps = createAsyncThunk(
  'gapAnalysis/fetchCriticalGaps',
  async (
    { tenantId, limit = 10 }: { tenantId: string; limit?: number },
    { rejectWithValue }
  ) => {
    try {
      return await gapAnalysisApi.getCriticalGaps(tenantId, limit);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch critical gaps'
      );
    }
  }
);

/**
 * Async thunk to fetch technique details
 */
export const fetchTechniqueDetails = createAsyncThunk(
  'gapAnalysis/fetchTechniqueDetails',
  async (
    { tenantId, techniqueId }: { tenantId: string; techniqueId: string },
    { rejectWithValue }
  ) => {
    try {
      return await gapAnalysisApi.getTechniqueGapDetails(tenantId, techniqueId);
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : 'Failed to fetch technique details'
      );
    }
  }
);

const gapAnalysisSlice = createSlice({
  name: 'gapAnalysis',
  initialState,
  reducers: {
    // Select tactic for viewing
    selectTactic: (state, action: PayloadAction<MitreTactic | null>) => {
      state.selectedTactic = action.payload;
    },

    // Select technique for viewing details
    selectTechnique: (state, action: PayloadAction<TechniqueGap | null>) => {
      state.selectedTechnique = action.payload;
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Reset state
    resetState: (state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    // Fetch Gap Report
    builder
      .addCase(fetchGapReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGapReport.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = action.payload.report;
        state.heatmapData = action.payload.heatmapData;
        state.clusters = action.payload.clusters;
        state.trends = action.payload.trends;
        state.statistics = action.payload.statistics;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchGapReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Critical Gaps
    builder
      .addCase(fetchCriticalGaps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCriticalGaps.fulfilled, (state, action) => {
        state.loading = false;
        if (state.currentReport) {
          state.currentReport.criticalGaps = action.payload;
        }
      })
      .addCase(fetchCriticalGaps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch Technique Details
    builder
      .addCase(fetchTechniqueDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTechniqueDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTechnique = action.payload;
      })
      .addCase(fetchTechniqueDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { selectTactic, selectTechnique, clearError, resetState } =
  gapAnalysisSlice.actions;

export default gapAnalysisSlice.reducer;
